// MiningSection.js
import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { LightningIcon } from '../assets/icons/Icons';
import { Icon } from '@iconify/react';
import fanImage from '../assets/images/FAN.png';

import DailyLoginModal from './DailyLoginModal';
import BoosterModal from './BoosterModal';
import { ModalContext, TelegramUserContext } from '../App';
import { useUserData } from '../contexts/UserDataContext';
import { rentMiner } from '../services/appwrite';
import { APP_CONFIG } from '../config/app';

const MiningContainer = styled.div`
  margin: 8px;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 8px;
  border-radius: 12px;
  width: calc(100% - 16px);
`;

const CalendarButton = styled.button`
  background-color: white;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 10px;
  left: 10px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const CircleContainer = styled.div`
  width: 70%;
  max-width: 220px;
  height: auto;
  aspect-ratio: 1/1;
  margin: 18px 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
`;

const FanImage = styled.div`
  width: 100%;
  height: 100%;
  background-color: transparent;
  position: relative;
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    animation: rotate ${props => props.$animationSpeed}s linear infinite;
    transition: all 0.3s ease;
    -webkit-user-drag: none;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    transform-origin: center center;
  }
  
  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const FloatingCoinContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 51%;
  transform: translate(-50%, -50%);
  z-index: 10;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FloatingCoinImage = styled.img`
  width: 74px;
  height: 74px;
  object-fit: contain;
  -webkit-user-drag: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.3));
`;


const MiningInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 16px;
  width: 100%;
  padding: 0 8px;
`;

const MiningAmount = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const HashRate = styled.div`
  font-size: 13px;
  color: #757575;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  width: 95%;
  margin-bottom: 8px;
`;

const Button = styled.button`
  flex: 1;
  padding: 10px 0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
`;

const ClaimButton = styled(Button)`
  background-color: white;
  border: none;
  color: black;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  
  &:active {
    transform: scale(0.98);
  }
`;

const BoosterButton = styled(Button)`
  background-color: black;
  border: none;
  color: white;
`;

const ClaimMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: rgba(16, 185, 129, 0.9); /* Updated green color */
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: bold;
  z-index: 1000;
  animation: claimAnimation 2s ease-in-out;
  display: flex; /* For aligning icon and text */
  align-items: center; /* For aligning icon and text */
  gap: 8px; /* Space between icon and text */
  
  @keyframes claimAnimation {
    0% { opacity: 0; transform: translate(-50%, -50%) scale(0.5); }
    50% { opacity: 1; transform: translate(-50%, -50%) scale(1.1); }
    100% { opacity: 0; transform: translate(-50%, -50%) scale(1); }
  }
`;

const OfflineMiningDisplay = styled.div`
  background-color: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
  padding: 12px;
  margin: 8px 0;
  text-align: center;
  animation: fadeInScale 0.5s ease-out;
  
  @keyframes fadeInScale {
    0% { opacity: 0; transform: scale(0.9); }
    100% { opacity: 1; transform: scale(1); }
  }
`;

const OfflineMiningTitle = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: #4CAF50;
  margin-bottom: 4px;
`;

const OfflineMiningAmount = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #2E7D32;
`;

const OfflineMiningTime = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 4px;
`;

const MinerPowerDisplay = styled.div`
  font-size: 12px;
  color: #757575;
  margin-top: 4px;
`;

const MiningSection = ({ onBalanceChange, initialUserData }) => {
  const [animationSpeed] = useState(2); // Constant moderate speed (removed setter)
  const [hashRate, setHashRate] = useState(33);
  const [showDailyLogin, setShowDailyLogin] = useState(false);
  const [showBooster, setShowBooster] = useState(false);
  const [minedAmount, setMinedAmount] = useState(0);
  const [minerPower, setMinerPower] = useState(1.0);
  const [isClaiming, setIsClaiming] = useState(false);
  const [showClaimMessage, setShowClaimMessage] = useState(false);
  const [claimMessage, setClaimMessage] = useState('');
  const [offlineMining, setOfflineMining] = useState(null);
  const [showOfflineMining, setShowOfflineMining] = useState(false);
  const [hasShownOfflineMining, setHasShownOfflineMining] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false); // Track if mining state is initialized
  
  // Get context values first before creating refs
  const { setIsDailyLoginOpen } = useContext(ModalContext);
  const telegramUser = useContext(TelegramUserContext);
  const { userData, claimSecondaryCoin, fetchUserData, updateServerMiningProgress } = useUserData();
  
  // Use refs to store current values for stable access
  const telegramUserRef = useRef(telegramUser);
  const lastUpdateTimeRef = useRef(Date.now());
  
  // Update refs when values change
  useEffect(() => {
    telegramUserRef.current = telegramUser;
  }, [telegramUser]);
  
  const CLAIM_MINIMUM = APP_CONFIG.MINIMUM_CLAIM_AMOUNT;

  // Initialize with cached data if available - can happen multiple times for new users
  useEffect(() => {
    const currentUserData = initialUserData || userData;
    if (currentUserData) {
      // For new users or when userData becomes available, always update state
      const currentMinedAmount = currentUserData.minedAmount || 0;
      const currentMinerPower = currentUserData.minerPower || 1.0;
      
      // Only set initialized to true if we have real user data (not just loading state)
      if (currentUserData.balance !== undefined || currentUserData.minedAmount !== undefined) {
        setMinedAmount(currentMinedAmount);
        setMinerPower(currentMinerPower);
        setHashRate(Math.round(currentMinerPower * 33));
        
        // Mark as initialized only when we have real data
        if (!isInitialized) {
          setIsInitialized(true);
          
        }
        
        // Show offline mining notification ONLY once and ONLY if it has meaningful mining
        if (!hasShownOfflineMining && 
            currentUserData.offlineMining && 
            currentUserData.offlineMining.minedWhileAway > 1.0 && // Only show if mined > 1 coin
            currentUserData.offlineMining.elapsedSeconds > 30) { // Only show if away > 30 seconds
          
          setOfflineMining(currentUserData.offlineMining);
          setShowOfflineMining(true);
          setHasShownOfflineMining(true);
          
          // Hide offline mining display after 5 seconds
          setTimeout(() => {
            setShowOfflineMining(false);
          }, 5000);
        }
        
        // Notify parent of balance if needed
        if (onBalanceChange && currentUserData.balance !== undefined) {
          onBalanceChange(currentUserData.balance);
        }
      }
    }
  }, [initialUserData, userData, onBalanceChange, hasShownOfflineMining, isInitialized]);

  // Update miner power and hash rate when userData changes
  useEffect(() => {
    const currentUserData = initialUserData || userData;
    if (currentUserData && isInitialized) {
      const newMinerPower = currentUserData.minerPower || 1.0;
      setMinerPower(newMinerPower);
      setHashRate(Math.round(newMinerPower * 33));
    }
  }, [initialUserData, userData, isInitialized]);
  
  // Server-side mining progress updates - visual animation only
  useEffect(() => {
    if (!isInitialized || !telegramUser?.id) {
      return;
    }

    // Visual animation only - increment locally for smooth UI
    const visualInterval = setInterval(() => {
      setMinedAmount(prev => prev + minerPower);
    }, 1000);

    // Handle page visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden && telegramUserRef.current?.id) {
        // User returned to the app - get latest from server
        const updateFromServer = async () => {
          const result = await updateServerMiningProgress(telegramUser.id);
          if (result?.success) {
            setMinedAmount(result.minedAmount);
            lastUpdateTimeRef.current = Date.now();
          }
        };
        updateFromServer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(visualInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isInitialized, telegramUser?.id, minerPower, updateServerMiningProgress]);
  // Handle claim action using optimized context function
    const handleRent = async (cost, powerIncrease) => {
    if (!telegramUser?.id) {
      return { success: false, error: 'User not found' };
    }

    const result = await rentMiner(telegramUser.id, cost, powerIncrease);
    if (result.success) {
      // Re-fetch user data to get the latest balance and miner power
      await fetchUserData(telegramUser.id);
    }
    return result;
  };

  const handleClaim = useCallback(async () => {
    if (!telegramUser?.id || minedAmount < CLAIM_MINIMUM || isClaiming) {
      return;
    }
    
    setIsClaiming(true);
    
    try {
      // First update mining progress from server to get the latest amount
      const updateResult = await updateServerMiningProgress(telegramUser.id);
      if (updateResult?.success) {
        setMinedAmount(updateResult.minedAmount);
        
        // Check if we still meet minimum after server update
        if (updateResult.minedAmount < CLAIM_MINIMUM) {
          setClaimMessage(`Minimum ${CLAIM_MINIMUM} coins required. You have ${updateResult.minedAmount.toFixed(2)}.`);
          setShowClaimMessage(true);
          setTimeout(() => setShowClaimMessage(false), 3000);
          return;
        }
      }
      
      // Proceed with claim using server-validated amount
      const result = await claimSecondaryCoin(telegramUser.id);
      
      if (result.success) {
        // Update local state immediately
        setMinedAmount(0);
        if (onBalanceChange) onBalanceChange(result.newBalance);
        
        // Show success message
        setClaimMessage(`+${result.claimedAmount.toFixed(2)} ${APP_CONFIG.SECONDARY_COIN_SYMBOL} Claimed!`);
        setShowClaimMessage(true);
        
        setTimeout(() => {
          setShowClaimMessage(false);
        }, 2000);
      } else {
        console.error('Claim failed:', result.error);
        setClaimMessage('Claim failed. Try again.');
        setShowClaimMessage(true);
        setTimeout(() => {
          setShowClaimMessage(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Error during claim:', error);
      setClaimMessage('Claim failed. Try again.');
      setShowClaimMessage(true);
      setTimeout(() => {
        setShowClaimMessage(false);
      }, 2000);    } finally {
      setIsClaiming(false);
    }
  }, [telegramUser?.id, minedAmount, isClaiming, claimSecondaryCoin, onBalanceChange, CLAIM_MINIMUM, updateServerMiningProgress]);

  const canClaim = minedAmount >= CLAIM_MINIMUM && !isClaiming;
  
  // Format time for offline mining display
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds.toFixed(0)} seconds`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)} minutes`;
    if (seconds < 86400) return `${(seconds / 3600).toFixed(1)} hours`;
    return `${(seconds / 86400).toFixed(1)} days`;
  };
  return (
    <MiningContainer>
      {showClaimMessage && (
        <ClaimMessage>
          <Icon icon="ph:check-circle-fill" width="20" height="20" />
          {claimMessage}
        </ClaimMessage>
      )}
      
      {showOfflineMining && offlineMining && (
        <OfflineMiningDisplay>
          <OfflineMiningTitle>Welcome back! 🎉</OfflineMiningTitle>
          <OfflineMiningAmount>+{offlineMining.minedWhileAway.toFixed(2)} ${APP_CONFIG.SECONDARY_COIN_SYMBOL} mined</OfflineMiningAmount>
          <OfflineMiningTime>while you were away for {formatTime(offlineMining.elapsedSeconds)}</OfflineMiningTime>
        </OfflineMiningDisplay>
      )}
      
      <CalendarButton 
        onClick={() => {
          setShowDailyLogin(true);
          setIsDailyLoginOpen(true);
          document.querySelectorAll('header, footer, nav, .HeaderWrapper').forEach(elem => {
            if (elem) elem.style.display = 'none';
          });
          document.querySelector('.app-container').style.filter = 'blur(2px)';
        }}
        style={{ boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}
      >
        <Icon icon="mynaui:calendar-solid" width="24" height="24" color="#9E9E9E" />
      </CalendarButton>
      
      <DailyLoginModal 
        isOpen={showDailyLogin} 
        onClose={() => {
          setShowDailyLogin(false);
          setIsDailyLoginOpen(false);
          document.querySelectorAll('header, footer, nav, .HeaderWrapper').forEach(elem => {
            if (elem) elem.style.display = '';
          });
          document.querySelector('.app-container').style.filter = '';
        }} 
      />
      
      <BoosterModal 
        isOpen={showBooster} 
        onClose={() => setShowBooster(false)} 
        userData={userData}
        onRent={handleRent}
      />
      
      <CircleContainer className="fan-container">
        <FanImage 
          $animationSpeed={animationSpeed}
        >
          <img 
            src={fanImage} 
            alt="Mining Fan" 
            className="rotating-fan"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        </FanImage>
        
        <FloatingCoinContainer>
          <FloatingCoinImage 
            src={APP_CONFIG.SECONDARY_COIN_IMAGE_PATH} 
            alt="Floating Coin" 
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        </FloatingCoinContainer>
      </CircleContainer>
      
      <MiningInfo>
        <MiningAmount>
          {minedAmount.toFixed(6)} ${APP_CONFIG.SECONDARY_COIN_SYMBOL}
          {minedAmount >= CLAIM_MINIMUM && ' ✅'}
        </MiningAmount>
        <HashRate>
          Hashrate: {hashRate} GH/s
          <LightningIcon />
        </HashRate>
        <MinerPowerDisplay>
          Mining: {minerPower.toFixed(1)} ${APP_CONFIG.SECONDARY_COIN_SYMBOL}/second
        </MinerPowerDisplay>
      </MiningInfo>
      
      <ButtonsContainer>
        <ClaimButton 
          $canClaim={canClaim}
          onClick={handleClaim}
          disabled={!canClaim}
        >
          {isClaiming ? 'Claiming...' : `Claim${minedAmount >= CLAIM_MINIMUM ? '' : ` (${CLAIM_MINIMUM} min)`}`}
        </ClaimButton>
        <BoosterButton onClick={() => setShowBooster(true)}>Booster</BoosterButton>
      </ButtonsContainer>
    </MiningContainer>
  );
};

export default MiningSection;
